import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AnimateButton from 'ui-component/extended/AnimateButton';

import usePartnerAuth from 'hooks/usePartnerAuth';

interface Props {
    open: boolean;
    onClose: () => void;
}

const PartnerLoginModal = ({ open, onClose }: Props) => {
    const { mpId = '' } = useParams();
    const { login } = usePartnerAuth();
    const theme = useTheme();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setError(null);
            setSubmitting(false);
        }
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        const result = await login(username.trim(), password, mpId);
        setSubmitting(false);
        if (result.error) {
            setError(result.error);
            return;
        }
        onClose();
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
            '&:hover fieldset': { borderColor: theme.palette.secondary.main },
            '&.Mui-focused fieldset': { borderColor: theme.palette.secondary.main }
        },
        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
        '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.secondary.main },
        '& .MuiOutlinedInput-input': { padding: '18px 14px', color: '#fff' }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a1a', borderRadius: 2 } }}>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ px: 4, pt: 4, pb: 4 }}>
                    {/* Title */}
                    <Typography
                        align="center"
                        sx={{
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                            fontSize: '1.25rem',
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                            mb: 2
                        }}
                    >
                        Partner Sign-in
                    </Typography>

                    {/* Marketing partner badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ backgroundColor: theme.palette.secondary.main, px: 1.5, py: 0.6, border: `1px solid ${theme.palette.secondary.main}`, borderRight: 'none', borderRadius: '6px 0 0 6px' }}>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', whiteSpace: 'nowrap' }}>
                                Marketing Partner
                            </Typography>
                        </Box>
                        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.15)', px: 1.5, py: 0.6, border: '1px solid rgba(255,255,255,0.15)', borderRadius: '0 6px 6px 0' }}>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', whiteSpace: 'nowrap' }}>
                                {mpId || '—'}
                            </Typography>
                        </Box>
                    </Box>

                    <Stack spacing={2}>
                        <TextField
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            autoFocus
                            fullWidth
                            required
                            sx={inputSx}
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            required
                            sx={inputSx}
                        />
                        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

                        <Box sx={{ mt: 1 }}>
                            <AnimateButton>
                                <Button
                                    type="submit"
                                    fullWidth
                                    size="large"
                                    variant="contained"
                                    disabled={submitting}
                                    sx={{
                                        backgroundColor: theme.palette.secondary.main,
                                        color: '#fff',
                                        boxShadow: 'none',
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        py: 1.5,
                                        '&:hover': { backgroundColor: theme.palette.secondary.main, opacity: 0.85 },
                                        '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' }
                                    }}
                                >
                                    {submitting ? 'Signing in…' : 'Sign in'}
                                </Button>
                            </AnimateButton>
                        </Box>
                    </Stack>
                </DialogContent>
            </form>
        </Dialog>
    );
};

export default PartnerLoginModal;
